// import apis and mutations
import { getPlanUnits } from 'RestAPIs/cost-centers';
import { mutations } from 'RestAPIs/rapini';
import { queryKeys } from 'generated/index';

// import components
import { Button, DialogActions, TextField } from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ControlledDatePicker,
  Dialog,
  LoadingOverlay,
  showSnackbar,
  VirtualizedAutocomplete
} from 'components/shared';

// import hooks
import {
  useGetOptionLabel,
  useRenderOption
} from 'components/shared/PlanUnitSelector/PlanUnitSelector.hooks';
import { useRosterFunctions } from 'hooks';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

// import utils
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';

// import schemas
import getSchema from 'components/shared/Assignments/PeriodAssignmentSchema';

// import styles
import { StyledGridContainer, StyledGridFullWidth } from '../Grid/Grid.styles';

// import types
import type { PlanUnitMix } from 'components/types';
import type {
  CreatePeriodAssignmentRequest,
  PeriodAssignmentResponse,
  UpdatePeriodAssignmentRequest
} from 'generated/index';
import type { SubmitHandler } from 'react-hook-form';
import type { InferType } from 'yup';

const schema = getSchema();
const defaultFormValues = {
  description: '',
  hoursPerWeek: 0,
  startDate: dayjs().startOf('day'),
  endDate: dayjs().endOf('year').startOf('day'),
  function: {},
  organizationalUnit: {}
};

type Props = {
  isOpen: boolean;
  isFlex: boolean;
  assignment?: PeriodAssignmentResponse;
  handleClose: () => void;
  onSave: () => void;
};

export type PeriodAssignmentForm = InferType<typeof schema>;

const getDefaultFormValues = (assignment) => {
  if (!assignment) {
    return defaultFormValues;
  }
  return assignment;
};

const PeriodAssignmentDialog = ({
  isOpen,
  isFlex,
  assignment,
  handleClose,
  onSave
}: Props) => {
  'use memo';

  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const {
    rosterFunctions: rosterFunctionOptions,
    isLoading: isLoadingRosterFunctions,
    isOptionDisabled: isRosterFunctionDisabled,
    renderOption: renderRosterFunctionOption,
    getOptionLabel: getRosterFunctionLabel
  } = useRosterFunctions();
  const [planUnitOptions, setPlanUnitOptions] = useState<PlanUnitMix[]>([]);
  const [isPlanUnitsLoading, setIsPlanUnitsLoading] = useState<boolean>(false);
  const getPlanUnitOptionLabel = useGetOptionLabel();

  const { control, handleSubmit, watch, reset } = useForm<PeriodAssignmentForm>(
    {
      defaultValues: getDefaultFormValues(assignment),
      resolver: yupResolver(schema)
    }
  );

  const { mutate: createAssignment, isLoading: isLoadingCreate } =
    mutations.useCreatePeriodAssignment({
      onSuccess: onSave
    });

  const { mutate: updateAssignment, isLoading: isLoadingUpdate } =
    mutations.useUpdatePeriodAssignment({
      onSuccess: onSave
    });

  useEffect(() => {
    if (assignment) {
      reset(getDefaultFormValues(assignment));
    } else {
      reset(defaultFormValues);
    }
  }, [assignment, reset]);

  const [watchStartDate, watchEndDate] = watch(['startDate', 'endDate']);

  const renderPlanUnitOption = useRenderOption({
    isMulti: false
  });

  useEffect(() => {
    const fetchPlanUnits = async () => {
      setIsPlanUnitsLoading(true);
      const { data } = await getPlanUnits({
        startDate: watchStartDate.toDate(),
        endDate: watchEndDate.toDate(),
        type: isFlex ? 'public' : 'employee'
      });
      setPlanUnitOptions(data);
      setIsPlanUnitsLoading(false);
    };
    fetchPlanUnits();
  }, [watchStartDate, watchEndDate, isFlex]);

  const onSubmit: SubmitHandler<PeriodAssignmentForm> = (data) => {
    const payload = {
      entityId: assignment?.entityId,
      description: data.description,
      hoursPerWeek: data.hoursPerWeek,
      startDate: data.startDate.format('YYYY-MM-DD'),
      endDate: data.endDate.format('YYYY-MM-DD'),
      functionId: data.function.id,
      organizationalUnitId:
        data.organizationalUnit?.brId ?? data.organizationalUnit?.id
    };
    if (assignment) {
      updateAssignment(payload as UpdatePeriodAssignmentRequest, {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKeys.getPeriodAssignments.name);
          showSnackbar({
            severity: 'success',
            message: t('periodAssignment.saved')
          });
        },
        onError: () => {
          const message = t('somethingWentWrong');
          showSnackbar({
            severity: 'error',
            message: message
          });
        }
      });
    } else {
      createAssignment(payload as CreatePeriodAssignmentRequest, {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKeys.getPeriodAssignments.name);
          showSnackbar({
            severity: 'success',
            message: t('periodAssignment.saved')
          });
        },
        onError: () => {
          const message = t('somethingWentWrong');
          showSnackbar({
            severity: 'error',
            message: message
          });
        }
      });
    }
  };

  return (
    <Dialog
      open={isOpen}
      title={
        !!assignment ? t('periodAssignment.edit') : t('periodAssignment.add')
      }
      handleClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            minWidth: 550
          }
        }
      }}
    >
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <StyledGridContainer>
          <Grid sx={{ flexGrow: 1 }}>
            <Controller
              name="organizationalUnit"
              control={control}
              render={({ field, fieldState }) => (
                <VirtualizedAutocomplete
                  {...field}
                  onChange={(e, v) => {
                    field.onChange(v || null);
                  }}
                  optionLabelProp="name"
                  getOptionLabel={(o) => getPlanUnitOptionLabel(o) ?? ''}
                  options={planUnitOptions}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      ref={params.InputProps.ref}
                      label={t('planUnit')}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message ?? null}
                      variant="outlined"
                      size="small"
                      required
                    />
                  )}
                  renderOption={renderPlanUnitOption}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  autoHighlight
                  loading={isPlanUnitsLoading}
                />
              )}
            />
          </Grid>
          <Grid sx={{ flexGrow: 1 }}>
            <Controller
              name="function"
              control={control}
              render={({ field, fieldState }) => (
                <VirtualizedAutocomplete
                  {...field}
                  onChange={(e, v) => {
                    field.onChange(v);
                  }}
                  optionLabelProp="description"
                  getOptionLabel={getRosterFunctionLabel}
                  getOptionDisabled={isRosterFunctionDisabled}
                  options={rosterFunctionOptions}
                  value={
                    assignment && 'function' in assignment
                      ? rosterFunctionOptions.find(
                          ({ id }) => id === assignment?.function?.id
                        )
                      : undefined
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      ref={params.InputProps.ref}
                      label={t('function')}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message ?? null}
                      variant="outlined"
                      size="small"
                      required
                    />
                  )}
                  renderOption={renderRosterFunctionOption}
                  isOptionEqualToValue={(option, value) => {
                    return option?.id === value?.id;
                  }}
                  autoHighlight
                  loading={isLoadingRosterFunctions}
                />
              )}
            />
          </Grid>
        </StyledGridContainer>
        <StyledGridContainer>
          <Grid>
            <ControlledDatePicker
              name="startDate"
              control={control}
              label={t('startDate')}
              variant="outlined"
              required
            />
          </Grid>
          <Grid>
            <ControlledDatePicker
              name="endDate"
              control={control}
              label={t('endDate')}
              variant="outlined"
              required
            />
          </Grid>
          <Grid>
            <Controller
              name="hoursPerWeek"
              control={control}
              render={({ field, fieldState }) => (
                <NumericFormat
                  {...field}
                  customInput={TextField}
                  type="text"
                  onValueChange={({ value: v }) =>
                    field.onChange({ target: { value: v } })
                  }
                  label={t('hoursPerWeek')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message ?? null}
                  variant="outlined"
                  size="small"
                  decimalSeparator=","
                  valueIsNumericString
                  required
                />
              )}
            />
          </Grid>
        </StyledGridContainer>
        <StyledGridContainer>
          <StyledGridFullWidth>
            <Controller
              name="description"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  fullWidth
                  {...field}
                  label={t('description')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message ?? null}
                  variant="outlined"
                  size="small"
                  required
                />
              )}
            />
          </StyledGridFullWidth>
        </StyledGridContainer>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="contained">
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoadingCreate || isLoadingUpdate}
          >
            {t('save')}
          </Button>
        </DialogActions>
        <LoadingOverlay loading={isLoadingCreate || isLoadingUpdate} />
      </form>
    </Dialog>
  );
};

export default PeriodAssignmentDialog;
